import React, { useState, useEffect } from 'react';
import { quizQuestions, calculatePreference } from '../quizData';
import QuestionScreen from './QuestionScreen';
import ResultsScreen from './ResultsScreen';
import './Quiz.css';

const Quiz = ({ onBackToHome }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [wines, setWines] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadWines();
  }, []);

  const loadWines = async () => {
    try {
      console.log('Loading wines from CSV...');
      const response = await fetch('/TBL_Intensity_Menu_NEW.csv');
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const csvText = await response.text();
      console.log('CSV loaded successfully, length:', csvText.length);
      console.log('CSV content preview:', csvText.substring(0, 200) + '...');
      
      const wines = parseCSV(csvText);
      console.log('Parsed wines:', wines.length, 'wines loaded');
      if (wines.length > 0) {
        console.log('First wine sample:', wines[0]);
        console.log('Available columns:', Object.keys(wines[0]));
      }
      setWines(wines);
    } catch (error) {
      console.error('Error loading wines:', error);
      // Fallback to old CSV if new one fails
      try {
        console.log('Trying fallback to old wines.csv...');
        const fallbackResponse = await fetch('/wines.csv');
        const fallbackCsvText = await fallbackResponse.text();
        const fallbackWines = parseCSV(fallbackCsvText);
        console.log('Fallback wines loaded:', fallbackWines.length);
        setWines(fallbackWines);
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
      }
    }
  };

  const parseCSV = (csvText) => {
    // Split by lines but handle quoted fields that contain line breaks
    const lines = [];
    let currentLine = '';
    let inQuotes = false;
    
    for (let i = 0; i < csvText.length; i++) {
      const char = csvText[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
        currentLine += char;
      } else if (char === '\n' && !inQuotes) {
        if (currentLine.trim()) {
          lines.push(currentLine.trim());
        }
        currentLine = '';
      } else {
        currentLine += char;
      }
    }
    
    // Add the last line if it exists
    if (currentLine.trim()) {
      lines.push(currentLine.trim());
    }
    
    const headers = parseCSVLine(lines[0]);
    console.log('CSV Headers:', headers);
    const wines = [];
    
    for (let i = 1; i < lines.length; i++) {
      if (lines[i].trim()) {
        const values = parseCSVLine(lines[i]);
        const wine = {};
        headers.forEach((header, index) => {
          wine[header.trim()] = values[index]?.trim();
        });
        wines.push(wine);
      }
    }
    
    console.log('Parsed wines sample:', wines.slice(0, 2));
    return wines;
  };

  // Helper function to parse CSV line with proper quote handling
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    result.push(current.trim());
    // Remove surrounding quotes but preserve line breaks within the field
    return result.map(field => {
      const trimmed = field.replace(/^"|"$/g, '');
      // Replace escaped line breaks with actual line breaks
      return trimmed.replace(/\\n/g, '\n');
    });
  };

  const handleAnswer = (answer) => {
    // Only add to answers if it's not a breaker screen (continue action)
    if (answer.value !== 'continue') {
      const newAnswers = [...answers, answer];
      setAnswers(newAnswers);
    }
    
    console.log('Answer submitted:', answer);
    console.log('Current question index:', currentQuestionIndex);
    console.log('Total questions:', quizQuestions.length);
    
    if (currentQuestionIndex < quizQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      console.log('Last question answered, showing loading...');
      setIsLoading(true);
      // Simulate loading time
      setTimeout(() => {
        console.log('Loading complete, showing results...');
        setIsLoading(false);
        setCurrentQuestionIndex(quizQuestions.length); // Move to results state
      }, 1000);
    }
  };

  const resetQuiz = () => {
    setCurrentQuestionIndex(0);
    setAnswers([]);
    setIsLoading(false);
  };

  const getRecommendations = () => {
    console.log('Calculating recommendations...');
    console.log('Answers:', answers);
    console.log('Wines available:', wines.length);
    
    if (wines.length === 0) {
      console.log('No wines loaded, returning empty array');
      return [];
    }
    
    console.log('Sample wine data:', wines[0]);
    console.log('Available columns in first wine:', Object.keys(wines[0]));
    
    const acidityPreference = calculatePreference(answers, 'acidity');
    const sweetnessPreference = calculatePreference(answers, 'sweetness');
    const bodyPreference = calculatePreference(answers, 'body');
    
    console.log('Preferences:', { acidityPreference, sweetnessPreference, bodyPreference });
    console.log('All answers by category:');
    console.log('Acidity answers:', answers.filter(a => a.category === 'acidity').map(a => a.value));
    console.log('Sweetness answers:', answers.filter(a => a.category === 'sweetness').map(a => a.value));
    console.log('Body answers:', answers.filter(a => a.category === 'body').map(a => a.value));

    const scoredWines = wines
      .map(wine => {
        let score = 0;
        
        // Check if we have the new CSV format or old format
        const hasNewFormat = wine['Acidity Index'] !== undefined;
        
        if (hasNewFormat) {
          console.log('Processing new format wine:', wine.Wine);
          console.log('Raw wine data:', {
            Wine: wine.Wine,
            Vintage: wine.Vintage,
            Color: wine.Color,
            Varietals: wine.Varietals,
            'Intensity Level': wine['Intensity Level'],
            'Acidity Index': wine['Acidity Index'],
            'Sweetness Index': wine['Sweetness Index'],
            'Body Index': wine['Body Index']
          });
          
          // Convert index values to preference levels for scoring
          const acidityLevel = getLevelFromIndex(wine['Acidity Index'], false);
          const sweetnessLevel = getLevelFromIndex(wine['Sweetness Index'], false);
          const bodyLevel = getLevelFromIndex(wine['Body Index'], true);
          
          console.log('Converted levels:', { acidityLevel, sweetnessLevel, bodyLevel });
          
          // Calculate match score
          if (acidityLevel === acidityPreference) score += 3;
          else if (
            (acidityPreference === 'high' && acidityLevel === 'medium') ||
            (acidityPreference === 'medium' && (acidityLevel === 'high' || acidityLevel === 'low')) ||
            (acidityPreference === 'low' && acidityLevel === 'medium')
          ) score += 1;
          
          if (sweetnessLevel === sweetnessPreference) score += 3;
          else if (
            (sweetnessPreference === 'high' && sweetnessLevel === 'medium') ||
            (sweetnessPreference === 'medium' && (sweetnessLevel === 'high' || sweetnessLevel === 'low')) ||
            (sweetnessPreference === 'low' && sweetnessLevel === 'medium')
          ) score += 1;
          
          if (bodyLevel === bodyPreference) score += 3;
          else if (
            (bodyPreference === 'full' && bodyLevel === 'medium') ||
            (bodyPreference === 'medium' && (bodyLevel === 'full' || bodyLevel === 'light')) ||
            (bodyPreference === 'light' && bodyLevel === 'medium')
          ) score += 1;
          
          // Create combined wine name with vintage
          const wineName = wine.Vintage && wine.Wine ? `${wine.Vintage} ${wine.Wine}` : wine.Wine || wine.name;
          
          // Determine if this is a blend (has multiple varietals)
          // Single varietals have "(100%)", blends have percentages < 100%
          // Blends have line breaks between varietals
          const isBlend = wine.Varietals && (
            wine.Varietals.includes('\n') || // Line breaks indicate multiple varietals
            wine.Varietals.includes(',') || 
            wine.Varietals.includes('&') || 
            wine.Varietals.includes('and') ||
            wine.Varietals.includes('+') ||
            wine.Varietals.includes(';') ||
            // Check for percentage patterns that indicate blends
            (wine.Varietals.includes('(') && wine.Varietals.includes(')') && 
             !wine.Varietals.includes('(100%)'))
          );
          
          console.log(`Wine: ${wine.Wine}, Varietals: "${wine.Varietals}", Is Blend: ${isBlend}`);
          console.log(`Varietals length: ${wine.Varietals ? wine.Varietals.length : 0}`);
          console.log(`Contains line break: ${wine.Varietals ? wine.Varietals.includes('\n') : false}`);
          console.log(`Contains 100%: ${wine.Varietals ? wine.Varietals.includes('(100%)') : false}`);
          console.log(`Raw varietals bytes:`, wine.Varietals ? Array.from(wine.Varietals).map(c => c.charCodeAt(0)) : []);
          
          return { 
            ...wine, 
            score,
            displayName: wineName,
            acidityLevel,
            sweetnessLevel,
            bodyLevel,
            isBlend
          };
        } else {
          // Old format fallback
          console.log('Processing old format wine:', wine.name);
          
          if (wine.acidity === acidityPreference) score += 3;
          else if (
            (acidityPreference === 'high' && wine.acidity === 'medium') ||
            (acidityPreference === 'medium' && (wine.acidity === 'high' || wine.acidity === 'low')) ||
            (acidityPreference === 'low' && wine.acidity === 'medium')
          ) score += 1;
          
          if (wine.sweetness === sweetnessPreference) score += 3;
          else if (
            (sweetnessPreference === 'high' && wine.sweetness === 'medium') ||
            (sweetnessPreference === 'medium' && (wine.sweetness === 'high' || wine.sweetness === 'low')) ||
            (sweetnessPreference === 'low' && wine.sweetness === 'medium')
          ) score += 1;
          
          if (wine.body === bodyPreference) score += 3;
          else if (
            (bodyPreference === 'full' && wine.body === 'medium') ||
            (bodyPreference === 'medium' && (wine.body === 'full' || wine.body === 'light')) ||
            (bodyPreference === 'light' && wine.body === 'medium')
          ) score += 1;
          
          return { 
            ...wine, 
            score,
            displayName: wine.name,
            acidityLevel: wine.acidity,
            sweetnessLevel: wine.sweetness,
            bodyLevel: wine.body,
            isBlend: false // Old format doesn't have blend info
          };
        }
      })
      .sort((a, b) => b.score - a.score);
    
    // Separate blends and single varietals
    const blends = scoredWines.filter(wine => wine.isBlend);
    const singleVarietals = scoredWines.filter(wine => !wine.isBlend);
    
    console.log('Blends found:', blends.length);
    console.log('Single varietals found:', singleVarietals.length);
    
    // Show some examples of what was detected
    if (blends.length > 0) {
      console.log('Sample blends detected:', blends.slice(0, 3).map(wine => ({
        name: wine.Wine,
        varietals: wine.Varietals,
        score: wine.score
      })));
    }
    
    if (singleVarietals.length > 0) {
      console.log('Sample single varietals detected:', singleVarietals.slice(0, 3).map(wine => ({
        name: wine.Wine,
        varietals: wine.Varietals,
        score: wine.score
      })));
    }
    
    // Ensure we get 3 blends and 3 single varietals (6 total recommendations)
    let recommendations = [];
    
    // Add top 3 blends (or all if less than 3)
    const topBlends = blends.slice(0, 3);
    recommendations.push(...topBlends);
    
    // Add top 3 single varietals (or all if less than 3)
    const topSingleVarietals = singleVarietals.slice(0, 3);
    recommendations.push(...topSingleVarietals);
    
    // If we don't have enough of either type, we'll have fewer than 6 total
    // This is fine - we prioritize quality matches over quantity
    
    console.log('Final recommendations:', recommendations);
    console.log('Blends in recommendations:', recommendations.filter(wine => wine.isBlend).length);
    
    return recommendations;
  };

  // Helper function to convert index values to preference levels
  const getLevelFromIndex = (index, isBody = false) => {
    const num = parseFloat(index);
    if (isNaN(num)) return isBody ? 'medium' : 'medium'; // fallback
    
    if (isBody) {
      // For body, convert to light/medium/full based on the new index scale
      if (num <= 50) return 'light';
      if (num <= 100) return 'medium';
      return 'full';
    } else {
      // For acidity and sweetness, convert to low/medium/high based on the new index scale
      if (num <= 30) return 'low';
      if (num <= 50) return 'medium';
      return 'high';
    }
  };

  console.log('Render state:', { isLoading, currentQuestionIndex, totalQuestions: quizQuestions.length, answersCount: answers.length });

  if (isLoading) {
    console.log('Showing loading screen');
    return (
      <div className="quiz-container">
        <div className="loading-screen">
          <h2>Calculating your perfect wine match...</h2>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (currentQuestionIndex >= quizQuestions.length) {
    console.log('Showing results screen');
    return (
      <ResultsScreen
        answers={answers}
        recommendations={getRecommendations()}
        onRestart={resetQuiz}
        onBackToHome={onBackToHome}
      />
    );
  }

  return (
    <div className="quiz-container">
      <QuestionScreen
        question={quizQuestions[currentQuestionIndex]}
        questionNumber={currentQuestionIndex + 1}
        totalQuestions={quizQuestions.length}
        onAnswer={handleAnswer}
        onBack={onBackToHome}
      />
    </div>
  );
};

export default Quiz;
